import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SimpleEditor from "@/components/ui/SimpleEditor";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { post, put } from "@rails/request.js";
import ProjectPhotos from "@/pages/sites/ProjectPhotos";
import ProjectFiles from "@/pages/sites/ProjectFiles";
import ProjectLinks from "@/pages/sites/ProjectLinks";
import { toast } from "sonner";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { removeProject } from "@/lib/api";
import { JsonEditor, type JsonObject } from "@/components/ui/JsonEditor";

type Section = {
  id: number;
  title: string;
  slug: string;
  [key: string]: any;
};

type Project = {
  id: number;
  title: string;
  subtitle?: string;
  home?: boolean;
  section_id: number;
  body: string;
  tag_list?: string;
  metadata?: JsonObject;
  [key: string]: any;
};

type ProjectFormProps = {
  section: Section;
  allSections: Section[];
  onProjectCreated: () => void;
  onClose: () => void;
  onDeleted?: () => void;
  site: any;
  project?: Project;
};

const projectMetadata = (metadata: unknown): JsonObject => {
  if (typeof metadata === "object" && metadata !== null && !Array.isArray(metadata)) {
    return metadata as JsonObject;
  }
  return {};
};

const ProjectForm: React.FC<ProjectFormProps> = ({
  section,
  allSections,
  onProjectCreated,
  onClose,
  onDeleted,
  site,
  project,
}) => {
  // removed useToast, using sonner's toast directly
  const [form, setForm] = useState({
    title: project?.title || "",
    subtitle: project?.subtitle || "",
    home: project?.home ?? false,
    section_id: project?.section_id ?? section.id,
    body: project?.body || "",
    tag_list: project?.tag_list || "",
    metadata: projectMetadata(project?.metadata),
  });
  const [metadataValid, setMetadataValid] = useState(true);

  React.useEffect(() => {
    if (project) {
      setForm({
        title: project.title || "",
        subtitle: project.subtitle || "",
        home: project.home ?? false,
        section_id: project.section_id ?? section.id,
        body: project.body || "",
        tag_list: project.tag_list || "",
        metadata: projectMetadata(project.metadata),
      });
    } else {
      // Reset form for new project
      setForm({
        title: "",
        subtitle: "",
        home: false,
        section_id: section.id,
        body: "",
        tag_list: "",
        metadata: {},
      });
    }
    setMetadataValid(true);
  }, [project, section.id]);

  const mutation = useMutation({
    mutationFn: async (params: any) => {
      if (project) {
        // Edit mode
        const response = await put(`/sites/${site.slug}/projects/${project.id}.json`, {
          body: JSON.stringify({ project: params }),
          responseKind: "json",
        });
        if (!response.ok) {
          const error = await response.json;
          throw new Error(error.errors ? error.errors.join(", ") : "Failed to update project");
        }
        return response.json;
      } else {
        // Create mode
        const response = await post(`/sites/${site.slug}/projects.json?section=${section.slug}`, {
          body: JSON.stringify({ project: params }),
          responseKind: "json",
        });
        if (!response.ok) {
          const error = await response.json;
          throw new Error(error.errors ? error.errors.join(", ") : "Failed to create project");
        }
        return response.json;
      }
    },
    onSuccess: () => {
      if (project) {
        toast("Project updated", {
          description: "The project was updated successfully.",
          action: {
            label: "Undo",
            onClick: () => {
              // Optionally implement undo logic here
            },
          },
        });
      } else {
        toast("Project created", {
          description: "The project was created successfully.",
          action: {
            label: "Undo",
            onClick: () => {
              // Optionally implement undo logic here
            },
          },
        });
      }
      if (!project) {
        // Reset form after creating a new project
        setForm({
          title: "",
          subtitle: "",
          home: false,
          section_id: section.id,
          body: "",
          tag_list: "",
          metadata: {},
        });
        onProjectCreated();
        onClose();
      } else {
        onProjectCreated();
        // Do NOT call onClose here; keep the form open after update
      }
    },
    onError: (error: any) => {
      toast("Project save failed", {
        description: error?.message || "Failed to save project.",
        action: {
          label: "Dismiss",
          onClick: () => {},
        },
      });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let checked = false;
    if (type === "checkbox" && "checked" in e.target) {
      checked = (e.target as HTMLInputElement).checked;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const [tab, setTab] = useState("details");

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <div className="px-2">
      <div className="overflow-auto h-[calc(100vh-72px)]">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="metadata">Metadata</TabsTrigger>
            <TabsTrigger value="images" disabled={!project}>Images</TabsTrigger>
            <TabsTrigger value="files" disabled={!project}>Files</TabsTrigger>
            <TabsTrigger value="videos" disabled={!project}>Videos</TabsTrigger>
          </TabsList>
          <TabsContent value="details">
            <form
              onSubmit={e => {
                e.preventDefault();
                if (!metadataValid) return;
                mutation.mutate(form);
              }}
              className="space-y-4 mb-2"
            >
              <h2 className="text-xl font-bold mb-2">
                {project ? `Edit project "${project.title}"` : `New project on "${section.title}" section`}
              </h2>
              <div className="text-xs text-muted-foreground mb-2">
                Section slug: <span className="font-mono">{section.slug}</span>
              </div>
              <div>
                <Label htmlFor="title">* Title</Label>
                <Input id="title" name="title" value={form.title} onChange={handleChange} required />
              </div>
              <div>
                <Label htmlFor="subtitle">Subtitle</Label>
                <Input id="subtitle" name="subtitle" value={form.subtitle} onChange={handleChange} />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="home" name="home" checked={form.home} onCheckedChange={checked => setForm(f => ({ ...f, home: !!checked }))} />
                <Label htmlFor="home">Home</Label>
                <span className="text-xs text-muted-foreground ml-2">Mark as start page.</span>
              </div>
              <div>
                <Label htmlFor="section_id">* Section</Label>
                <Select
                  value={String(form.section_id)}
                  onValueChange={value => setForm(f => ({ ...f, section_id: Number(value) }))}
                  name="section_id"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select section" />
                  </SelectTrigger>
                  <SelectContent>
                    {allSections.map(section => (
                      <SelectItem key={section.id} value={String(section.id)}>
                        {section.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="body">* Body</Label>
                <SimpleEditor
                  id="body"
                  name="body"
                  value={form.body}
                  onChange={(value: string) => setForm(f => ({ ...f, body: value }))}
                  required
                  className="min-h-[200px] mb-2"
                />
                <span className="text-xs text-muted-foreground">
                  Write the text of your project, you can upload images and embed video
                </span>
              </div>
              <div>
                <Label htmlFor="tag_list">Tag list</Label>
                <Input id="tag_list" name="tag_list" value={form.tag_list} onChange={handleChange} />
              </div>
              {mutation.isError && (
                <Alert variant="destructive" className="mb-2">
                  {(mutation.error as Error).message}
                </Alert>
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={mutation.isPending || !metadataValid}>
                  {mutation.isPending
                    ? project ? "Saving..." : "Creating..."
                    : project ? "Save Changes" : "Create Project"}
                </Button>
                {project && (
                  <>
                    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                      <AlertDialogTrigger asChild>
                        <Button
                          type="button"
                          variant="destructive"
                          disabled={deleting}
                          onClick={() => setShowDeleteDialog(true)}
                        >
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Project</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this project? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel
                            disabled={deleting}
                            onClick={() => setShowDeleteDialog(false)}
                          >
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            disabled={deleting}
                            onClick={async () => {
                              setDeleting(true);
                              try {
                                await removeProject(site.slug, project.id);
                                toast("Project deleted", {
                                  description: "The project was deleted successfully.",
                                });
                                setShowDeleteDialog(false);
                                if (onDeleted) onDeleted();
                                else onClose();
                              } catch (err: any) {
                                toast("Delete failed", {
                                  description: err?.message || "Failed to delete project.",
                                });
                              } finally {
                                setDeleting(false);
                              }
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </>
                )}
              </div>
            </form>
          </TabsContent>
          <TabsContent value="metadata">
            <form
              onSubmit={event => {
                event.preventDefault();
                if (!metadataValid) return;
                mutation.mutate(form);
              }}
              className="space-y-4 mb-2"
            >
              <div>
                <h2 className="text-xl font-bold mb-1">Project metadata</h2>
                <p className="text-xs text-muted-foreground">
                  Structured properties that templates can use to compose project-specific UI.
                </p>
              </div>
              <JsonEditor
                value={form.metadata}
                onChange={(metadata) => setForm((current) => ({ ...current, metadata }))}
                onValidityChange={setMetadataValid}
                disabled={mutation.isPending}
              />
              {mutation.isError && (
                <Alert variant="destructive" className="mb-2">
                  {(mutation.error as Error).message}
                </Alert>
              )}
              <Button type="submit" disabled={mutation.isPending || !metadataValid}>
                {mutation.isPending
                  ? project ? "Saving..." : "Creating..."
                  : project ? "Save Metadata" : "Create Project"}
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="images">
            {!project ? (
              <Alert variant="default" className="mb-4">
                Save the project first to manage images.
              </Alert>
            ) : (
              <ProjectPhotos site={site} project={project} />
            )}
          </TabsContent>
          <TabsContent value="files">
            {!project ? (
              <Alert variant="default" className="mb-4">
                Save the project first to manage files.
              </Alert>
            ) : (
              <ProjectFiles site={site} project={project} />
            )}
          </TabsContent>
          <TabsContent value="videos">
            {!project ? (
              <Alert variant="default" className="mb-4">
                Save the project first to manage videos and links.
              </Alert>
            ) : (
              <ProjectLinks site={site} project={project} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProjectForm;
